/* ---------------------------------------------------------------------------
 * `_bc_4c` BACKFILL — teach carts built before 2026-08-15 to speak the packed
 * form the cart transform now prices from.
 *
 * WHY THIS EXISTS
 * The diamond-pricing cart transform used to read three separate line
 * attributes — `Diamond Weight`, `Diamond Color`, `Diamond Clarity`. Its input
 * query sits at Shopify's complexity ceiling of exactly 30/30, so the three
 * reads were collapsed into ONE packed attribute, `_bc_4c` = "weight|color|
 * clarity", and the three were REMOVED from the query. There is no function-side
 * fallback and there cannot be one: adding the fields back overflows the budget
 * and the extension will not deploy.
 *
 * Every theme writer emits the pack today (assets/diamond-customizer.js,
 * assets/bc-diamond-filters.js, snippets/cart-gift-unlock.liquid). Lines added
 * BEFORE that theme went live carry the three legacy properties and no pack, and
 * a Shopify cart survives for weeks. Left alone, each of those lines breaks in
 * two ways once the new function is deployed:
 *
 *   1. LABEL LOSS — every legacy line. lineExpand REPLACES a line's attributes
 *      with whatever the function returns, and the function rebuilds the three
 *      Diamond properties by unpacking `_bc_4c`. No pack, no properties: the
 *      order reaches the workshop and the ERP with no spec on the line.
 *
 *   2. UNDERCHARGE — collection-card quick-adds, and this one is money.
 *      bc-diamond-filters.js stamps `_expected_price` with the BARE VARIANT
 *      price, because with a pack present the function is authoritative and
 *      computes variant + stone itself. Without a pack it is not authoritative,
 *      the fail-safe test `frontendPriceCents > variantPriceCents` is false, no
 *      operation is emitted, and the line checks out at the bare mounting price.
 *      In the repo's own fixture that is a $1,049 mounting sold with ~$1,020 of
 *      diamond attached to it for nothing.
 *
 * Waiting for old carts to drain does not close this. assets/cart-share.js
 * rebuilds a cart verbatim from a stored snapshot with no expiry on the link, so
 * a share link sent last week re-creates pack-less lines every time it is
 * opened. (That path is repacked at its own door — see `withPack` there.)
 *
 * WHAT IT WRITES
 * Byte-for-byte the pack the live writers produce for the same line: three
 * segments, always, joined on "|", empty string for a field the product does not
 * have. A partial pack is not a compromise — "2.5 Cts||" is exactly what
 * diamond-customizer.js sends for a product with a weight selector and no
 * quality selector, and the function reads it as an incomplete spec, declines to
 * price it from the matrix, and falls through to the `_expected_price`
 * fail-safe. Identical to what the three separate reads produced. What the
 * partial pack DOES buy is the labels in (1) above, which are lost without it.
 *
 * TWO REFUSALS, both matching the writers:
 *   - a value containing "|" OMITS the pack rather than stripping the character.
 *     A stripped "VS|2" would be sold, invoiced and manufactured as "VS2";
 *     omitting only costs the matrix price and leaves the three human properties
 *     carrying the true spec.
 *   - a line with none of the three properties gets nothing. There is no spec to
 *     pack, and a pack that is not exactly three segments is read by the function
 *     as a TAMPER SIGNAL that kills the free gift for the whole cart.
 *
 * PROPERTY SURGERY, so it is written defensively. /cart/change.js REPLACES a
 * line's whole properties hash — the existing properties are read back from
 * /cart.js and copied forward key for key, because dropping one would destroy a
 * ring size, an engraving, `_expected_price`, `_pc_selection`, or the `_bc_*`
 * tracking carriers. Nothing else about the line is sent, so quantity and
 * variant are untouched.
 *
 * WHERE IT RUNS: every themed page load, deferred, from layout/theme.liquid —
 * the same placement cart-gift.js uses and for the same reason. A cart-page-only
 * backfill would miss the shopper who browses to a product and presses Checkout
 * from the drawer, which is the exact path the undercharge rides. RESIDUAL GAP:
 * a shopper who opens a saved /checkout URL, a wallet resume link, or an
 * abandoned-cart email that lands straight on checkout runs no theme JS at all
 * and is never repacked. Nothing theme-side can reach them.
 *
 * COST: one GET /cart.js per page load until a scan comes back clean, then
 * nothing for the rest of the session. Writes only ever touch lines that
 * actually need one.
 * ------------------------------------------------------------------------- */
(function () {
  'use strict';

  var KEY_WEIGHT = 'Diamond Weight';
  var KEY_COLOR = 'Diamond Color';
  var KEY_CLARITY = 'Diamond Clarity';
  var KEY_PACK = '_bc_4c';
  var KEY_GIFT = '_free_gift';

  var MEMO_CLEAN = 'bc_4c_repack_clean';
  var MEMO_FAILS = 'bc_4c_repack_fails';
  var MEMO_RELOADED = 'bc_4c_repack_reloaded';

  // Bounds. MAX_LINES caps one page load's writes so a pathological cart cannot
  // fire fifty mutations; the remainder is picked up on the next load, because a
  // run that changed something never counts as a failure. MAX_FAILS stops a cart
  // that cannot be repacked at all (Shopify refusing the write, a variant that
  // has since sold out) from re-trying on every page of the visit.
  var MAX_LINES = 20;
  var MAX_FAILS = 3;
  var START_DELAY_MS = 400;

  function routeRoot() {
    try {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    } catch (e) {
      return '/';
    }
  }

  function text(value) {
    return value === null || value === undefined ? '' : String(value);
  }

  /**
   * The pack this line is MISSING, or '' when it must not be written.
   *
   * Every refusal is deliberate:
   *   - already packed (any non-empty `_bc_4c`) — nothing to do, and rewriting it
   *     would let this module fight the writer that put it there. This is also
   *     what makes the whole module idempotent.
   *   - `_free_gift: 'true'` — the gift line. It is priced at $0.00 by the
   *     transform and carries no spec worth backfilling, and its marker is the
   *     only thing standing between "gift" and "$299 sale" on the order. Not
   *     ours to touch. Any OTHER marker value ('declined') sits on an ordinary
   *     product line with real money on it: that line IS repacked, and the marker
   *     is copied forward untouched with the rest of its properties.
   *   - a pipe anywhere in the three values, and
   *   - no legacy spec at all (accessories, gift cards)
   *     — see the module header.
   */
  function packFor(properties) {
    var props = properties || {};
    if (text(props[KEY_PACK]) !== '') return '';
    if (text(props[KEY_GIFT]) === 'true') return '';
    var segments = [text(props[KEY_WEIGHT]), text(props[KEY_COLOR]), text(props[KEY_CLARITY])];
    var probe = segments.join('');
    if (probe === '') return '';
    if (probe.indexOf('|') !== -1) return '';
    return segments.join('|');
  }

  /**
   * Everything this line already carries, plus the pack. Empty values are dropped
   * the way cart-gift.js drops them — Shopify does not keep a blank line-item
   * property, so sending one back is noise at best.
   */
  function withPack(properties, pack) {
    var merged = {};
    var source = properties || {};
    for (var k in source) {
      if (!Object.prototype.hasOwnProperty.call(source, k)) continue;
      var v = source[k];
      if (v === null || v === undefined || v === '') continue;
      merged[k] = String(v);
    }
    merged[KEY_PACK] = pack;
    return merged;
  }

  // ---- session memo -------------------------------------------------------
  // sessionStorage, not localStorage: a cart outlives a session by weeks, so a
  // durable "clean" flag would still be sitting there tomorrow in front of a cart
  // that has since been rebuilt from a share link.
  function memoGet(key) {
    try {
      return sessionStorage.getItem(key) || '';
    } catch (e) {
      return '';
    }
  }
  function memoSet(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (e) {}
  }

  function onCartPage() {
    try {
      return /^\/cart(\/|$)/.test(location.pathname);
    } catch (e) {
      return false;
    }
  }

  /** Is the shopper looking at the drawer right now? Dawn marks it `active`. */
  function drawerOpen() {
    try {
      var drawer = document.querySelector('cart-drawer');
      return !!(drawer && drawer.classList && drawer.classList.contains('active'));
    } catch (e) {
      return false;
    }
  }

  function getCart() {
    return fetch(routeRoot() + 'cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (r) {
      return r.ok ? r.json() : null;
    });
  }

  /**
   * How many lines in this cart still need a pack.
   *
   * Used to confirm a write actually LANDED. `/cart/change.js` answering 200 with
   * a cart body is not proof the property stuck — and if it did not, `changed`
   * would still be counted, MEMO_FAILS would be reset, and every page load for the
   * rest of the session would re-issue the same doomed write. Comparing the count
   * before and after turns that into an ordinary failure, which MAX_FAILS bounds.
   */
  function dirtyCount(cart) {
    var items = (cart && cart.items) || [];
    var n = 0;
    for (var i = 0; i < items.length; i++) {
      if (packFor(items[i].properties)) n++;
    }
    return n;
  }

  /**
   * Which rendered sections this page can repaint from a change response.
   *
   * A repack CHANGES WHAT THE SHOPPER IS CHARGED on a collection-card line (back
   * up to the price they were shown when they added it), and the drawer sitting
   * in the DOM was rendered before that happened. Asking for the sections costs
   * nothing on a page that has none — the cart page has neither the drawer nor
   * the header bubble, and gets a plain reload instead.
   */
  var SECTION_TARGETS = [
    { id: 'CartDrawer', section: 'cart-drawer', selector: '#CartDrawer' },
    { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
  ];

  function sectionsToRequest() {
    var wanted = [];
    for (var i = 0; i < SECTION_TARGETS.length; i++) {
      if (document.getElementById(SECTION_TARGETS[i].id)) wanted.push(SECTION_TARGETS[i].section);
    }
    return wanted;
  }

  /**
   * Repaint the drawer and the cart bubble from a change response — the same
   * swap Dawn performs on any cart mutation, minus the `open()` cart-drawer.js
   * pairs with it. NEVER while the drawer is open: replacing its contents under a
   * shopper who is reading them is worse than a stale price they are about to see
   * corrected at checkout anyway.
   */
  function paintSections(parsed) {
    if (!parsed || !parsed.sections) return;
    if (drawerOpen()) return;
    try {
      var parser = new DOMParser();
      for (var i = 0; i < SECTION_TARGETS.length; i++) {
        var target = document.getElementById(SECTION_TARGETS[i].id);
        var html = parsed.sections[SECTION_TARGETS[i].section];
        if (!target || !html) continue;
        var source = parser.parseFromString(html, 'text/html').querySelector(SECTION_TARGETS[i].selector);
        if (source) target.innerHTML = source.innerHTML;
      }
    } catch (e) {}
  }

  /**
   * Write one line's merged properties. Resolves with the updated cart, or null
   * on any failure — Shopify answers a refused change with a `status` envelope
   * rather than a rejected promise, so both shapes are folded into the same
   * "nothing happened" answer. A failed repack leaves the line exactly as it was.
   */
  function writePack(item, pack, sections) {
    var body = { id: item.key, properties: withPack(item.properties, pack) };
    if (sections && sections.length) {
      body.sections = sections;
      body.sections_url = window.location.pathname;
    }
    return fetch(routeRoot() + 'cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (parsed) {
        if (!parsed || parsed.status) return null;
        return parsed;
      })
      .catch(function () {
        return null;
      });
  }

  function findByKey(cart, key) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].key === key) return items[i];
    }
    return null;
  }

  /**
   * One pass over the cart.
   *
   * Sequential, not Promise.all — the same reasoning cart-gift.js writes down:
   * each write is a cart mutation and Shopify serialises them anyway, and going
   * in order keeps a half-finished run easy to reason about (some lines packed,
   * which is strictly progress, rather than an unpredictable subset).
   *
   * Each write is re-derived from the LATEST cart we hold — the previous change
   * response, which is a full cart — not from the snapshot taken at the top.
   * Changing a line's properties changes its key, and more to the point
   * cart-gift.js may have written its stand-down marker onto one of these very
   * lines in the meantime; re-reading means we copy forward what is there NOW.
   */
  function run() {
    if (memoGet(MEMO_CLEAN) === '1') return Promise.resolve();
    if (parseInt(memoGet(MEMO_FAILS) || '0', 10) >= MAX_FAILS) return Promise.resolve();

    return getCart()
      .then(function (cart) {
        // A cart we could not READ is not a cart we know to be clean. `getCart`
        // answers null for any non-2xx — a 429 while Shopify is rate-limiting, a
        // 5xx, a captcha interstitial. Treating that as "no dirty lines" would
        // memoise CLEAN off a failed request and switch the backfill off for the
        // whole session, which is exactly the visit where an undercharge then
        // rides through unrepaired. Say nothing and try again next page load.
        if (!cart || !cart.items) return null;

        var items = cart.items;
        var keys = [];
        for (var i = 0; i < items.length && keys.length < MAX_LINES; i++) {
          if (packFor(items[i].properties)) keys.push(items[i].key);
        }
        if (!keys.length) {
          // Empty cart, or every line already speaks the packed form. Nothing to
          // do for the rest of this session.
          memoSet(MEMO_CLEAN, '1');
          return null;
        }

        var sections = sectionsToRequest();
        var latest = cart;
        var changed = 0;
        var chain = Promise.resolve();

        keys.forEach(function (key) {
          chain = chain.then(function () {
            var item = findByKey(latest, key);
            if (!item) return null; // line removed, or merged into a twin
            var pack = packFor(item.properties);
            if (!pack) return null; // someone else packed it while we waited
            return writePack(item, pack, sections).then(function (updated) {
              if (!updated) return null;
              // 200 + a cart body is not proof the property stuck. Only advance
              // when the cart really does carry one fewer pack-less line.
              if (dirtyCount(updated) >= dirtyCount(latest)) return null;
              changed++;
              latest = updated;
              paintSections(updated);
              return null;
            });
          });
        });

        return chain.then(function () {
          if (!changed) {
            // Found work and could not do it. Count it, so a cart Shopify keeps
            // refusing does not cost a round trip on every page of the visit.
            memoSet(MEMO_FAILS, String(parseInt(memoGet(MEMO_FAILS) || '0', 10) + 1));
            return null;
          }
          memoSet(MEMO_FAILS, '0');
          // The cart page renders its prices server-side and has no drawer to
          // repaint, so the honest refresh is a reload — the idiom cart-share.js
          // already uses after it rebuilds a cart. ONCE PER SESSION: if a write
          // reports success but the pack does not stick, the next load would find
          // the same dirty line and reload again, forever.
          if (onCartPage() && memoGet(MEMO_RELOADED) !== '1') {
            memoSet(MEMO_RELOADED, '1');
            try {
              location.reload();
            } catch (e) {}
          }
          return null;
        });
      })
      .catch(function () {
        return null;
      });
  }

  // ---- boot ---------------------------------------------------------------
  // Deferred past first paint, and past the moment cart-gift.js does its own
  // property surgery on connectedCallback: both modules rewrite whole property
  // hashes, so the one that reads later wins, and reading later is the safer half
  // of the pair. Never long enough to matter — the shopper has to reach a
  // checkout button before it lands for the delay to cost anything.
  function boot() {
    if (window.__bcCart4cRepackBooted) return;
    window.__bcCart4cRepackBooted = true;
    if (!window.fetch || !window.Promise) return;
    setTimeout(function () {
      try {
        run();
      } catch (e) {}
    }, START_DELAY_MS);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Exposed for tests, and for cart-share.js — which rebuilds cart lines from a
  // stored snapshot and must pack them at its own door rather than wait for the
  // next page load (no bundler in this theme).
  window.BcCart4cRepack = { PACK_KEY: KEY_PACK, packFor: packFor, withPack: withPack, run: run };
})();
